import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useCollectionBinding, useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useSiteCtaStyles } from '../hooks/useSiteCtaStyles';

// ============================================================================
// Types
// ============================================================================

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
}

interface BedroomCategory {
  key: string;
  label: string;   // "One Bedrooms", "Two Bedrooms"
  display: string; // The big numeric character shown above the label ("1", "2", …) or "S" for studios
  param: string;   // URL query param value
}

// ============================================================================
// Bedroom grouping
// ============================================================================

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  const names = ['studio', 'one', 'two', 'three', 'four', 'five'];
  return names[num] || `${num}`;
}

function keyToLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studios',
    one: 'One Bedrooms',
    two: 'Two Bedrooms',
    three: 'Three Bedrooms',
    four: 'Four Bedrooms',
    five: 'Five Bedrooms',
  };
  return labels[key] || `${key} Bedrooms`;
}

function keyToDisplay(key: string): string {
  const numeric: Record<string, string> = {
    studio: 'S',
    one: '1',
    two: '2',
    three: '3',
    four: '4',
    five: '5',
  };
  return numeric[key] || key;
}

function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const groups: Record<string, true> = {};
  for (const plan of floorplans) {
    const key = bedCountToKey(plan.floorplan_beds_min);
    groups[key] = true;
  }
  const sorted = Object.keys(groups).sort((a, b) => {
    const ai = BED_ORDER.indexOf(a);
    const bi = BED_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
  return sorted.map((key) => ({
    key,
    label: keyToLabel(key),
    display: keyToDisplay(key),
    param: key,
  }));
}

// ============================================================================
// Slot group definitions
// ============================================================================

// Background — the property's featured image sits behind everything, with a
// primary-color overlay layered on top so the centered copy stays readable
// regardless of what the property photo looks like.
const backgroundDef: SlotGroupDefinition = {
  label: 'Background Image',
  description: 'Full-bleed background photo. A primary-color overlay is layered on top for readability.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
  },
};

// Primary "View All Floor Plans" CTA — links to the /floor-plans page by
// default. Editors can override the URL / label / target.
const ctaLinkDef: SlotGroupDefinition = {
  label: 'View All Floor Plans Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'View All Floor Plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function FloorplansList() {
  // Dynamic floor plan collection — the cards below are derived from this
  // list, so any change to the property's floor plans automatically flows
  // through to what visitors see.
  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });

  const categories = useMemo(
    () => (floorplans?.length ? groupFloorplansByBeds(floorplans) : []),
    [floorplans],
  );

  const bg = useSlotGroup('floorplanslist.background', backgroundDef);
  const cta = useSlotGroup('floorplanslist.cta_link', ctaLinkDef);

  useRevealStyles();
  useSiteCtaStyles();
  const headerRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });

  const hasCategories = categories.length > 0;

  return (
    <section className="relative overflow-hidden py-20 md:py-28 text-center">
      {/* Full-bleed background image */}
      <EditableSlotGroup
        groupPath="floorplanslist.background"
        definition={backgroundDef}
        className="absolute inset-0"
      >
        {bg.image ? (
          <VibesitesImage
            url={bg.image}
            alt=""
            sizes="100vw"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        ) : (
          <div className="absolute inset-0 bg-[var(--vibe-color-primary)]" />
        )}
      </EditableSlotGroup>

      {/* Primary-color overlay (~80% opacity) — sits between the photo and the
          content so the centered white copy always has enough contrast. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[var(--vibe-color-primary)] opacity-80"
      />

      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className="reveal-fade-up">
          <RichTextContent
            path="floorplanslist.title"
            label="Title"
            as="h2"
            className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-[var(--vibe-color-text-on-primary,#ffffff)] mb-6 leading-[1.05]"
          />
          {/* Subtitle + tagline are hard-coded to white — the section always
              sits on top of the primary-color overlay, which is dark on this
              theme (and generally intended to be dark enough for white copy),
              so the `text-on-primary` token isn't a safe pick: some property
              overrides set that token to a mid-tone that fails contrast
              against the overlay. White guarantees the copy stays legible on
              every property, matching what the title already renders as via
              its fallback. */}
          <RichTextContent
            path="floorplanslist.subtitle"
            label="Subtitle"
            as="div"
            className="font-body text-base sm:text-lg text-white leading-relaxed mb-6 [&_a]:text-white [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:decoration-2"
          />
          <RichTextContent
            path="floorplanslist.tagline"
            label="Tagline"
            as="p"
            className="font-body text-base sm:text-lg italic text-white mb-10"
          />
        </div>

        {hasCategories && (
          <div
            ref={gridRef}
            className="grid justify-items-center gap-x-12 gap-y-10 sm:gap-x-16 md:gap-x-24 mb-12"
            style={{
              // Equal-width columns so the middle bedroom sits on the same
              // center axis as the CTA. Intrinsic flex widths (e.g. "Studios"
              // vs "Two Bedrooms") shift that middle item off-center.
              gridTemplateColumns: `repeat(${categories.length}, minmax(0, 1fr))`,
            }}
          >
            {categories.map((cat) => (
              <a
                key={cat.key}
                data-reveal
                href={`/floor-plans?bedroom=${cat.param}`}
                className="reveal-fade-up group inline-flex max-w-full flex-col items-center text-center text-[var(--vibe-color-text-on-primary,#ffffff)] transition-opacity duration-200 hover:opacity-90"
              >
                <span className="font-heading text-7xl sm:text-8xl md:text-9xl font-bold leading-none mb-3">
                  {cat.display}
                </span>
                <span className="font-body text-lg sm:text-xl font-semibold border-b-2 border-[var(--vibe-color-text-on-primary,#ffffff)] pb-2 transition-[border-color,opacity] duration-200 group-hover:opacity-80">
                  {cat.label}
                </span>
              </a>
            ))}
          </div>
        )}

        <div className="flex w-full justify-center">
          <EditableSlotGroup
            groupPath="floorplanslist.cta_link"
            definition={ctaLinkDef}
          >
            <a
              href={cta.url || '/floor-plans'}
              target={cta._target || '_self'}
              className="site-cta site-cta--primary floorplanslist-cta"
              style={{
                // The CTA sits on the primary-color overlay, so its outline
                // flips to the on-primary text token — otherwise the default
                // "primary outline on any background" recipe would render
                // invisibly. Hover colors are overridden below to match
                // Footer `.footer-cta-button` (surface fill + contrast ink).
                ['--site-cta-primary-accent' as string]:
                  'var(--vibe-color-text-on-primary, #ffffff)',
                ['--site-cta-primary-on-accent' as string]: 'var(--vibe-color-primary)',
              }}
            >
              {cta.label || 'View All Floor Plans'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Match Footer › cta_contact_link (.footer-cta-button) hover. */
        a.site-cta.floorplanslist-cta:hover,
        a.site-cta.floorplanslist-cta:focus-visible {
          background: var(--vibe-color-surface, #fff);
          border-color: var(--vibe-color-surface, #fff);
          color: var(--site-cta-ink-on-surface, #fff);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Make Yourself At Home</p>',
    },
    'floorplanslist.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_home_floorplans_blurb"></span></p>',
    },
    'floorplanslist.tagline': {
      label: 'Tagline',
      type: 'richtext',
      defaultValue: '<p>Choose your desired floor plan or view all.</p>',
    },
  },
  slotGroups: {
    'floorplanslist.background': backgroundDef,
    'floorplanslist.cta_link': ctaLinkDef,
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorplansList as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default FloorplansList;
